import { useState, useEffect, useMemo } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { useAuth } from '@/modules/auth'
import { useSubscription } from '@/modules/subscriptions'
import { SubscriptionsApi } from '@/shared/api/subscriptions'
import { Pricing } from '@/ui/components/landing/pricing-section-with-comparison'
import { Button } from '@/ui/components'
import { Sparkles, CheckCircle, MoveRight } from '@/ui/icons'

export default function PricingPage() {
  const [searchParams] = useSearchParams()
  const checkoutSuccess = searchParams.get('checkout') === 'success'

  const { isAuthenticated, getAccessToken } = useAuth()
  const { isPro, loading: subLoading } = useSubscription()

  const api = useMemo(() => new SubscriptionsApi(getAccessToken), [getAccessToken])

  const [interval, setInterval] = useState<'annual' | 'monthly'>('annual')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (checkoutSuccess) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [checkoutSuccess])

  const handleCheckout = async () => {
    if (!isAuthenticated) return
    setLoading(true)
    setError(null)
    try {
      const { url } = await api.createCheckoutSession(interval)
      window.location.href = url
    } catch (err) {
      setError((err as Error).message ?? 'Something went wrong. Please try again.')
      setLoading(false)
    }
  }

  const handlePortal = async () => {
    setLoading(true)
    setError(null)
    try {
      const { url } = await api.createPortalSession()
      window.location.href = url
    } catch (err) {
      setError((err as Error).message ?? 'Could not open billing portal.')
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Success banner */}
      {checkoutSuccess && (
        <div className="bg-adventure-600 text-white py-4 px-6 text-center">
          <div className="flex items-center justify-center gap-2 font-medium">
            <CheckCircle className="h-5 w-5" />
            You're now on Pro — welcome to the full experience!
          </div>
        </div>
      )}

      {/* Existing pricing comparison table from landing page */}
      <Pricing />

      {/* CTA section for authenticated users */}
      {isAuthenticated && !subLoading && (
        <div className="bg-earth-50 pb-20">
          <div className="container mx-auto px-6 max-w-xl text-center space-y-4">
            {error && (
              <p className="text-sm text-red-600 bg-red-50 rounded-md px-4 py-2">{error}</p>
            )}

            {isPro ? (
              <>
                <p className="text-sm text-muted-foreground">You're on Pro. Manage your plan or cancel anytime.</p>
                <Button
                  variant="outline"
                  onClick={handlePortal}
                  disabled={loading}
                  className="w-full sm:w-auto"
                >
                  {loading ? 'Opening portal…' : 'Manage Billing'}
                </Button>
              </>
            ) : (
              <>
                <div className="flex rounded-lg border p-1 gap-1 max-w-xs mx-auto">
                  <button
                    type="button"
                    onClick={() => setInterval('annual')}
                    className={`flex-1 py-1.5 text-sm rounded-md font-medium transition-colors ${
                      interval === 'annual'
                        ? 'bg-adventure-600 text-white'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Annual <span className="text-xs opacity-75">$39.99/yr</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setInterval('monthly')}
                    className={`flex-1 py-1.5 text-sm rounded-md font-medium transition-colors ${
                      interval === 'monthly'
                        ? 'bg-adventure-600 text-white'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Monthly <span className="text-xs opacity-75">$4.99/mo</span>
                  </button>
                </div>

                <Button
                  className="w-full sm:w-auto bg-adventure-600 hover:bg-adventure-700 text-white px-8"
                  onClick={handleCheckout}
                  disabled={loading}
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  {loading ? 'Redirecting to checkout…' : 'Start 14-Day Free Trial'}
                </Button>
                <p className="text-xs text-muted-foreground">Cancel anytime. Billed via Stripe.</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* CTA for unauthenticated users */}
      {!isAuthenticated && (
        <div className="bg-earth-50 pb-20">
          <div className="container mx-auto px-6 max-w-xl text-center">
            <Link to="/signup">
              <Button className="bg-adventure-600 hover:bg-adventure-700 text-white px-8 gap-2">
                Get Started Free <MoveRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
